import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { articles } from "../data/articles";
import { industryDetails } from "../data/industries";
import SEO from "../components/SEO";
import { ArrowRight, ArrowLeft, BookOpen, Search } from "lucide-react";

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function ResourcesPage() {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const industries = useMemo(
    () => Object.values(industryDetails).filter((i) => articles.some((a) => a.industry === i.slug)),
    []
  );

  const filtered = useMemo(() => {
    return articles.filter((a) => {
      const matchesFilter = filter === "all" || a.industry === filter;
      const q = query.trim().toLowerCase();
      const matchesQuery =
        !q ||
        a.title.toLowerCase().includes(q) ||
        a.excerpt.toLowerCase().includes(q);
      return matchesFilter && matchesQuery;
    });
  }, [query, filter]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Resources & Guides — BuyerBeacon Pro"
        description="Playbooks, case studies, and tactical guides for finding ready buyers across 12+ industries. Direct-mail activation, intent data, and audience-building tutorials."
        path="/resources"
      />
      <Navbar />
      <main className="pt-28">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="pointer-events-none absolute left-1/2 top-[60px] -z-10 h-[320px] w-[640px] -translate-x-1/2 rounded-full bg-[#d4af37]/12 blur-[120px]" />
          <div className="mx-auto max-w-6xl px-5 py-12 lg:px-8 lg:py-16">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Resources
            </p>
            <h1 className="mt-3 max-w-3xl text-5xl font-medium tracking-tight text-[#1e1b4b] sm:text-6xl">
              Playbooks for the people
              <br />
              <span className="text-slate-400">closing real revenue.</span>
            </h1>
            <p className="mt-5 max-w-2xl text-[16px] leading-relaxed text-slate-600">
              Practical, vertical-specific guides on customer acquisition, direct
              mail, intent data, and audience strategy — written for small and
              mid-sized businesses, and the agencies that serve them.
            </p>

            {/* search + filters */}
            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search articles…"
                  className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-10 pr-3 text-sm text-[#1e1b4b] outline-none transition-colors focus:border-[#1e1b4b]/40 focus:ring-2 focus:ring-[#d4af37]/20"
                />
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <FilterPill active={filter === "all"} onClick={() => setFilter("all")}>
                All ({articles.length})
              </FilterPill>
              {industries.map((i) => {
                const count = articles.filter((a) => a.industry === i.slug).length;
                return (
                  <FilterPill
                    key={i.slug}
                    active={filter === i.slug}
                    onClick={() => setFilter(i.slug)}
                  >
                    {i.name} ({count})
                  </FilterPill>
                );
              })}
            </div>
          </div>
        </section>

        {/* Articles grid */}
        <section className="border-t border-slate-200/80 bg-slate-50/40 py-16">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            {filtered.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center">
                <p className="text-sm text-slate-500">No articles match your search.</p>
              </div>
            ) : (
              <>
                {featured && <FeaturedCard article={featured} />}
                {rest.length > 0 && (
                  <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                    {rest.map((a) => (
                      <ArticleCard key={a.slug} article={a} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function FilterPill({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
        active
          ? "bg-[#1e1b4b] text-white"
          : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

function FeaturedCard({ article }) {
  const industry = industryDetails[article.industry];
  return (
    <Link
      to={`/resources/${article.slug}`}
      className="group block overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all hover:-translate-y-1 hover:shadow-[0_30px_70px_-30px_rgba(30,27,75,0.25)]"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="relative flex min-h-[280px] items-center justify-center bg-gradient-to-br from-[#1e1b4b] to-[#312e81] p-10">
          <div className="pointer-events-none absolute inset-0 opacity-25" style={{
            backgroundImage:
              "radial-gradient(circle at 30% 30%, rgba(212,175,55,0.6), transparent 60%), radial-gradient(circle at 70% 70%, rgba(212,175,55,0.25), transparent 50%)"
          }} />
          <div className="relative text-center">
            <BookOpen className="mx-auto h-10 w-10 text-[#d4af37]" />
            <p className="mt-3 text-[10.5px] font-semibold uppercase tracking-[0.2em] text-white/70">
              Featured Playbook
            </p>
          </div>
        </div>
        <div className="flex flex-col justify-center p-8 lg:p-10">
          {industry && (
            <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[#1e1b4b]/[0.06] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#1e1b4b]">
              {industry.name}
            </span>
          )}
          <h2 className="mt-4 text-3xl font-semibold tracking-tight text-[#1e1b4b]">
            {article.title}
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{article.excerpt}</p>
          <div className="mt-6 flex items-center justify-between">
            <span className="text-xs text-slate-500">
              {fmtDate(article.date)} · {article.readTime} min read
            </span>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1e1b4b] group-hover:text-[#d4af37]">
              Read playbook
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function ArticleCard({ article }) {
  const industry = industryDetails[article.industry];
  return (
    <Link
      to={`/resources/${article.slug}`}
      className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-[0_24px_50px_-20px_rgba(30,27,75,0.18)]"
    >
      {industry && (
        <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[#1e1b4b]/[0.06] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#1e1b4b]">
          {industry.name}
        </span>
      )}
      <h3 className="mt-4 text-lg font-semibold leading-snug text-[#1e1b4b]">
        {article.title}
      </h3>
      <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-slate-600">
        {article.excerpt}
      </p>
      <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
        <span className="text-xs text-slate-500">
          {fmtDate(article.date)} · {article.readTime} min
        </span>
        <ArrowRight className="h-4 w-4 text-slate-300 transition-all group-hover:translate-x-0.5 group-hover:text-[#d4af37]" />
      </div>
    </Link>
  );
}
